import { Target, Eye, Heart } from "lucide-react";
import { TEAM_MEMBERS } from "@/data/siteData";
import { CTABanner } from "@/components/CTABanner";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3 animate-fade-in-up">
            Our Story
          </p>
          <h1
            data-testid="about-heading"
            className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-4 animate-fade-in-up animation-delay-100"
          >
            About Coffice 24x7
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto font-body leading-relaxed animate-fade-in-up animation-delay-200">
            We're on a mission to make flexible, affordable workspaces accessible to
            every professional, startup, and team in Pune.
          </p>
        </div>
      </section>

      {/* Story */}
      <section data-testid="about-story" className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-600 mb-3">
                How It Started
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6">
                Born from a Simple Idea
              </h2>
              <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed font-body">
                <p>
                  Coffice 24x7 started in 2020 when our founder, Arjun Mehta, noticed a
                  gap in Pune's workspace market. Freelancers were working from noisy cafes,
                  startups were locked into expensive leases, and remote workers craved
                  community.
                </p>
                <p>
                  The idea was simple: create workspaces that are affordable, accessible
                  24 hours a day, and built around community. Starting with a single
                  center in Pimple Saudagar, we've grown to 5+ locations across Pune,
                  serving over 500 members.
                </p>
                <p>
                  Every Coffice center is designed with intention — from the ergonomic
                  furniture to the high-speed internet, from the quiet zones to the
                  collaboration areas. We believe your workspace should inspire your
                  best work.
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1626187890723-04ce3dbdc42a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3dvcmtpbmclMjBzcGFjZSUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NDc1NTh8MA&ixlib=rb-4.1.0&q=85"
                alt="Coffice workspace"
                className="w-full h-80 lg:h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section data-testid="about-mission-vision" className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                <Target className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                Our Mission
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                To provide flexible, affordable, and inspiring workspaces that
                empower professionals and teams to do their best work — anytime,
                anywhere in Pune.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center mb-5">
                <Eye className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                Our Vision
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                To become India's most trusted coworking network, enabling a new
                generation of startups, freelancers, and enterprises to thrive
                through the power of community and flexibility.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                <Heart className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                Our Values
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Community first. Flexibility always. Quality in every detail.
                We believe great work happens when people have the right space,
                tools, and support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="about-team" className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3">
              The People
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
              Meet Our Team
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
            {TEAM_MEMBERS.map((member) => (
              <div
                key={member.name}
                data-testid={`team-member-${member.initials.toLowerCase()}`}
                className="text-center group"
              >
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-slate-100 shadow-lg transition-all duration-300 group-hover:border-orange-200">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                      <span className="font-heading text-2xl font-bold text-slate-500">
                        {member.initials}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">
                  {member.name}
                </h3>
                <p className="text-sm text-orange-500 font-medium mb-2">
                  {member.role}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed max-w-xs mx-auto">
                  {member.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Join Our Community"
        subtitle="Become part of Pune's most vibrant coworking network. 500+ members and growing."
        buttonText="Get Started"
        buttonLink="/contact"
      />
    </div>
  );
}
